// Feature — Pixel birds flying out of forest overlay tiles.
//
// Purely stateless: bird positions are computed from (q, r, nowMs) only,
// so no per-frame allocation and no persistent particle state.

import { hash01 } from './noise.mjs';
import { hexKey, getNeighbor } from '../../math/hexMath.mjs';

/** Count adjacent tiles that carry a forest overlay. */
function _hasEnoughForestNeighbours(tile, tiles, minCount) {
  if (!tiles) return true; // fallback: no map provided, allow all
  const { q, r } = tile.coord;
  let count = 0;
  for (let s = 0; s < 6; s++) {
    const nb = getNeighbor(q, r, s);
    const nbTile = tiles.get(hexKey(nb.q, nb.r));
    if (nbTile && nbTile.overlaySpriteKey && nbTile.overlaySpriteKey.startsWith('overlays/forest/')) {
      if (++count >= minCount) return true;
    }
  }
  return false;
}

const BIRD_COUNT      = 6;      // birds per flock
const FLOCK_PERIOD_MS = 7000;   // full cycle length per tile (staggered per tile)
const FLOCK_LIFE_MS   = 3000;   // how long each flock is visible within the cycle

export class BirdsFx {
  /**
   * @param {CanvasRenderingContext2D} ctx
   * @param {Array<{tile:object, sp:{x:number,y:number}}>} visibleTiles
   * @param {number} worldSeed
   * @param {number} sz   – hex radius in screen pixels
   * @param {number} nowMs
   * @param {Map<string,object>} tiles – full tile map for neighbour lookup
   */
  render(ctx, visibleTiles, worldSeed, sz, nowMs, tiles) {
    for (const { tile, sp } of visibleTiles) {
      if (!tile.overlaySpriteKey) continue;
      if (!tile.overlaySpriteKey.startsWith('overlays/forest/')) continue;
      // Only emit birds from forest tiles that have ≥3 adjacent forest overlays
      if (!_hasEnoughForestNeighbours(tile, tiles, 3)) continue;
      this._drawFlock(ctx, tile.coord.q, tile.coord.r, sp.x, sp.y, sz, worldSeed, nowMs);
    }
  }

  _drawFlock(ctx, q, r, cx, cy, sz, worldSeed, nowMs) {
    // Stagger flock start times so every forest tile fires at a different moment
    const phaseShift = hash01(q, r, worldSeed) * FLOCK_PERIOD_MS;
    const cycleT     = (nowMs + phaseShift) % FLOCK_PERIOD_MS;

    if (cycleT > FLOCK_LIFE_MS) return; // tile is in its idle window

    const prog = cycleT / FLOCK_LIFE_MS; // 0 → 1 over the flock's lifetime

    // Alpha envelope: fade in → full → fade out
    let flockAlpha;
    if      (prog < 0.08) flockAlpha = prog / 0.08;
    else if (prog > 0.80) flockAlpha = (1 - prog) / 0.20;
    else                  flockAlpha = 1.0;

    if (flockAlpha < 0.02) return;

    ctx.save();
    ctx.fillStyle = '#1a1a1a';

    // Pixel size scales with zoom: 1 px normally, 2 px when zoomed in
    const ps = sz > 70 ? 2 : 1;

    for (let i = 0; i < BIRD_COUNT; i++) {
      // Per-bird deterministic seeds
      const b1 = hash01(q * 11 + i, r * 7,      worldSeed + 101); // scatter angle
      const b2 = hash01(i * 17,     q * 5 + i,  worldSeed + 202); // orbit speed
      const b3 = hash01(r * 13 + i, i * 23,     worldSeed + 303); // scatter distance

      const scatterAngle = b1 * Math.PI * 2;

      let bx, by;

      if (prog < 0.45) {
        // ── SWARM PHASE ──
        // Birds emerge from the canopy and circle tightly above the tile.
        const t = prog / 0.45;                      // 0 → 1
        const riseCy  = cy - t * sz * 0.40;         // centre rises as birds emerge
        const orbitA  = scatterAngle + t * Math.PI * (1.4 + b2 * 1.2);
        const orbitR  = sz * (0.05 + t * 0.20 + b3 * 0.06);
        bx = riseCy; // placeholder — computed below
        bx = cx      + Math.cos(orbitA) * orbitR;
        by = riseCy  + Math.sin(orbitA) * orbitR * 0.50; // flatten to look natural

      } else {
        // ── SCATTER PHASE ──
        // Each bird peels off toward its unique scatter direction.
        const t = (prog - 0.45) / 0.55; // 0 → 1

        // Bird's position at the end of the swarm phase (prog = 0.45, t_swarm = 1)
        const swarmA  = scatterAngle + Math.PI * (1.4 + b2 * 1.2);
        const swarmR  = sz * (0.05 + 0.20 + b3 * 0.06);
        const startX  = cx + Math.cos(swarmA) * swarmR;
        const startY  = (cy - sz * 0.40) + Math.sin(swarmA) * swarmR * 0.50;

        // Fly outward: ease-in so birds accelerate away
        const flyDist  = t * t * sz * (1.2 + b3 * 1.4);
        const flyAngle = scatterAngle + (b2 - 0.5) * 0.9; // slight direction variance
        bx = startX + Math.cos(flyAngle) * flyDist;
        by = startY + Math.sin(flyAngle) * flyDist * 0.70;
      }

      // Wing beat: simple on/off driven by time + per-bird phase
      const wingUp = Math.sin(nowMs / 150 + b1 * Math.PI * 4) > 0;

      ctx.globalAlpha = flockAlpha * 0.85;
      _drawPixelBird(ctx, Math.round(bx), Math.round(by), wingUp, ps);
    }

    ctx.restore();
  }
}

/**
 * Draw a tiny pixelated bird silhouette.
 *   wingUp=true  →  M shape (wings raised)
 *   wingUp=false →  W shape (wings dipped)
 *   ps           →  pixel size (1 or 2)
 */
function _drawPixelBird(ctx, x, y, wingUp, ps) {
  if (wingUp) {
    //  █   █       (wingtips up)
    //   █ █
    ctx.fillRect(x - 2 * ps, y - ps, ps, ps); // left tip
    ctx.fillRect(x - ps,     y,      ps, ps); // left inner
    ctx.fillRect(x,          y,      ps, ps); // body
    ctx.fillRect(x + ps,     y,      ps, ps); // right inner
    ctx.fillRect(x + 2 * ps, y - ps, ps, ps); // right tip
  } else {
    //   █ █         (wingtips dipped)
    //  █   █
    ctx.fillRect(x - 2 * ps, y,      ps, ps); // left tip
    ctx.fillRect(x - ps,     y - ps, ps, ps); // left inner
    ctx.fillRect(x,          y - ps, ps, ps); // body
    ctx.fillRect(x + ps,     y - ps, ps, ps); // right inner
    ctx.fillRect(x + 2 * ps, y,      ps, ps); // right tip
  }
}
